import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useLang } from "../lib/i18n";

type StatusKind = "Shipped" | "Building" | "Next" | "Considering" | "Won't ship";

function StatusPill({ kind }: { kind: StatusKind }) {
  const dotClass = {
    Shipped: "bg-emerald-500",
    Building: "bg-amber-500",
    Next: "bg-blue-500",
    Considering: "bg-slate-500",
    "Won't ship": "bg-red-500",
  }[kind];

  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-ink/[0.04] px-3 py-1.5 text-xs font-medium text-ink/70 dark:bg-paper/[0.08] dark:text-paper/75">
      <span className={`h-2 w-2 rounded-full ${dotClass}`} aria-hidden="true" />
      <span>{kind}</span>
    </span>
  );
}

const SHIPPED_ITEMS = [
  {
    item: "Windows support",
    notes: "WASAPI loopback + NSIS installer + portable `.exe`.",
  },
  {
    item: "In-meeting copilot",
    notes: "Question detection + answer drafting on Pro tier; Stripe billing + Cognito entitlement.",
  },
  {
    item: "Local offline translation",
    notes: "Reuses llama.cpp + Qwen 2.5 7B via local llama-server; DeepL still supported as fallback.",
  },
  {
    item: "Export to Markdown / Obsidian",
    notes: "Copy / save / send-to-vault, EN or PT.",
  },
  {
    item: "LLM summarization",
    notes: "Local via llama.cpp + Qwen 2.5 7B, batched on stop.",
  },
  {
    item: "Speaker diarization",
    notes: "pyannote.audio 3.1 via Python sidecar, batched on stop.",
  },
  {
    item: "MVP — capture, STT, translate, store",
    notes: "The foundation.",
  },
];

const BUILDING_NEXT_ITEMS: { item: string; status: StatusKind; eta: string }[] = [
  {
    item: "Code-signing + notarization (macOS) + Authenticode (Windows)",
    status: "Building",
    eta: "shipping with the next desktop GA",
  },
  {
    item: "Notion export",
    status: "Next",
    eta: "M2",
  },
  {
    item: "Linux support",
    status: "Considering",
    eta: "no commitment",
  },
];

const WONT_SHIP_ITEMS = [
  {
    title: "A bot that joins your call.",
    body: "That's the thing we're explicitly not. If your workflow needs that, Otter and Fireflies are good.",
  },
  {
    title: "Cloud transcription.",
    body: "Same answer — cloud is what most of our users are explicitly leaving.",
  },
  {
    title: "Mobile app.",
    body: "The product is a desktop copilot for desktop calls.",
  },
  {
    title: "Custom on-prem deployment for enterprise.",
    body: "After we hit 100 Pro.",
  },
];

export function Roadmap() {
  const lang = useLang();
  const isEnglish = lang === "en";

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Public roadmap — what we're shipping next"
        description="What's shipped, what's next, and why. Public, honest, and updated as we ship."
        path={isEnglish ? "/roadmap" : undefined}
        noindex={!isEnglish}
        alternateLangs={["en"]}
      />
      <Header />

      <main className="flex-1">
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              The plan we ship against.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              Minuta is built in the open: every feature on this page either just shipped,
              is being built right now, or is the next thing in the queue. We update this
              page when something moves — not weekly, not monthly, when it actually changes.
            </p>
          </div>
        </section>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-16">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Status legend
              </h2>
              <p className="mt-3 text-ink/70 dark:text-paper/70">
                What each roadmap status means.
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                <StatusPill kind="Shipped" />
                <StatusPill kind="Building" />
                <StatusPill kind="Next" />
                <StatusPill kind="Considering" />
                <StatusPill kind="Won't ship" />
              </div>
              <ul className="mt-7 space-y-2 text-[15px] text-ink/75 dark:text-paper/75">
                <li>Shipped — deployed in the latest desktop release.</li>
                <li>Building — actively in-progress this week.</li>
                <li>Next — the immediate next bet (≤ 30 days out).</li>
                <li>Considering — under evaluation. Not committed.</li>
                <li>Won't ship — explicitly out of scope and why.</li>
              </ul>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-16">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Shipped
              </h2>
              <p className="mt-3 text-ink/70 dark:text-paper/70">Most recent first.</p>

              <div className="mt-8 overflow-x-auto rounded-2xl border border-ink/10 dark:border-paper/12">
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-ink/[0.04] dark:bg-paper/[0.04]">
                    <tr>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink/65 dark:text-paper/65">
                        Item
                      </th>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink/65 dark:text-paper/65">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink/8 dark:divide-paper/10">
                    {SHIPPED_ITEMS.map((row) => (
                      <tr key={row.item}>
                        <td className="px-5 py-3.5 font-medium text-ink/85 dark:text-paper/85">
                          {row.item}
                        </td>
                        <td className="px-5 py-3.5 text-ink/75 dark:text-paper/75">
                          {row.notes}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-16">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Building / Next
              </h2>

              <div className="mt-8 overflow-x-auto rounded-2xl border border-ink/10 dark:border-paper/12">
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-ink/[0.04] dark:bg-paper/[0.04]">
                    <tr>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink/65 dark:text-paper/65">
                        Item
                      </th>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink/65 dark:text-paper/65">
                        Status
                      </th>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink/65 dark:text-paper/65">
                        ETA
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink/8 dark:divide-paper/10">
                    {BUILDING_NEXT_ITEMS.map((row) => (
                      <tr key={row.item}>
                        <td className="px-5 py-3.5 font-medium text-ink/85 dark:text-paper/85">
                          {row.item}
                        </td>
                        <td className="px-5 py-3.5 text-ink/75 dark:text-paper/75">
                          <StatusPill kind={row.status} />
                        </td>
                        <td className="px-5 py-3.5 text-ink/75 dark:text-paper/75">
                          {row.eta}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-16">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                Won't ship (yet)
              </h2>
              <p className="mt-4 text-lg text-ink/75 dark:text-paper/75">
                We get asked. We don't think it's right yet. We'll re-evaluate.
              </p>
              <ul className="mt-8 space-y-4">
                {WONT_SHIP_ITEMS.map((item) => (
                  <li key={item.title} className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80">
                    <span className="font-medium text-ink dark:text-paper">{item.title}</span> {item.body}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        <Reveal>
          <section>
            <div className="mx-auto max-w-3xl px-6 py-20">
              <p className="text-[15px] leading-relaxed text-ink/75 dark:text-paper/75">
                Want something on this list? File it, vote on it, or just email us. GitHub issues (public tracker coming soon) · <a href="mailto:hello@minutapp.tech" className="underline decoration-ink/30 underline-offset-4 hover:decoration-ink/65 dark:decoration-paper/35 dark:hover:decoration-paper/75">hello@minutapp.tech</a>
              </p>
            </div>
          </section>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
